import React, { useState, useEffect, useCallback } from "react";
import {
  Button, Dialog, Slide, Box, Chip, TextField, Typography, InputAdornment, Checkbox, Tooltip,
  IconButton, Menu, MenuItem
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import IndeterminateCheckBoxRoundedIcon from "@mui/icons-material/IndeterminateCheckBoxRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LeaveRequestForm from "./LeaveRequestForm";
import MandateLeaveForm from "./MandateLeaveForm";
import DynamicTable from "../../DynamicComponent/DynamicTable";
import { fetchData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import { exportToCSV, filterRows } from "../../utils/tableUtils";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const STATUS_CONFIG = {
  Excused:    { color: "#4caf50", bg: "#e8f5e9" },
  "Un-Excused": { color: "#f44336", bg: "#fdecea" },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { color: "#8F95B2", bg: "#f3f4f6" };
  return (
    <Chip
      label={status}
      size="small"
      sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: "11px", borderRadius: "20px" }}
    />
  );
};

const RowMenu = ({ row, onEdit, onDelete }) => {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        PaperProps={{ sx: { borderRadius: "10px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", minWidth: 130 } }}>
        <MenuItem onClick={() => { setAnchor(null); }} sx={{ fontSize: "13px" }}>View</MenuItem>
        <MenuItem onClick={() => { setAnchor(null); onEdit && onEdit(row); }} sx={{ fontSize: "13px" }}>Edit</MenuItem>
        <MenuItem onClick={() => { setAnchor(null); onDelete && onDelete(row); }} sx={{ fontSize: "13px", color: "#f44336" }}>Delete</MenuItem>
      </Menu>
    </>
  );
};

const fallback = [
  { leaveId: "LV001", leaveFrom: "12/02/2025", leaveTo: "12/05/2025", totalDays: 3, reason: "Medical", status: "Excused", appliedDate: "11/02/2025" },
  { leaveId: "LV002", leaveFrom: "15/03/2025", leaveTo: "16/03/2025", totalDays: 1, reason: "Personal", status: "Excused", appliedDate: "14/03/2025" },
  { leaveId: "LV003", leaveFrom: "20/04/2025", leaveTo: "22/04/2025", totalDays: 2, reason: "Family Function", status: "Un-Excused", appliedDate: "18/04/2025" },
  { leaveId: "LV004", leaveFrom: "01/05/2025", leaveTo: "03/05/2025", totalDays: 2, reason: "Medical", status: "Excused", appliedDate: "30/04/2025" },
  { leaveId: "LV005", leaveFrom: "10/05/2025", leaveTo: "12/05/2025", totalDays: 2, reason: "Personal", status: "Un-Excused", appliedDate: "08/05/2025" },
];

const dataColumns = [
  { label: "Leave ID", field: "leaveId" },
  { label: "Leave From", field: "leaveFrom" },
  { label: "Leave To", field: "leaveTo" },
  { label: "Total Days", field: "totalDays" },
  { label: "Reason", field: "reason" },
  { label: "Status", field: "status" },
  { label: "Applied Date", field: "appliedDate" },
  { label: "Actions", field: "actions" },
];

const LeaveRequest = () => {
  const [open, setOpen] = useState(false);
  const [mandateOpen, setMandateOpen] = useState(false);
  const [rawData, setRawData] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filterText, setFilterText] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadLeaves = useCallback(async () => {
    try {
      const applicationId = sessionStorage.getItem("applicationId") || "";
      const url = applicationId ? `student/leave/list?applicationId=${applicationId}` : "student/leave/list";
      const response = await fetchData(url);
      if (response?.data) {
        const data = Array.isArray(response.data) ? response.data : response.data.data || [];
        const mapped = data.map((r, i) => ({
          ...r,
          leaveId: r.leaveId
            ? (String(r.leaveId).startsWith("LV") ? r.leaveId : "LV" + String(r.leaveId).replace(/\D/g, "").padStart(3, "0"))
            : "LV" + String(i + 1).padStart(3, "0"),
        }));
        setRawData(mapped.length > 0 ? mapped : fallback);
      } else {
        setRawData(fallback);
      }
    } catch (_) {
      setRawData(fallback);
    }
  }, []);

  useEffect(() => { loadLeaves(); }, [loadLeaves]);

  const handleToggleSelect = (leaveId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(leaveId)) next.delete(leaveId);
      else next.add(leaveId);
      return next;
    });
  };

  const filteredRaw = filterRows(rawData, filterText);

  const allSelected = filteredRaw.length > 0 && filteredRaw.every((r) => selectedIds.has(r.leaveId));
  const someSelected = !allSelected && filteredRaw.some((r) => selectedIds.has(r.leaveId));

  const handleSelectAll = () => {
    if (allSelected) {
      // Deselect all
      setSelectedIds(new Set());
    } else {
      // Select all visible rows
      setSelectedIds(new Set(filteredRaw.map((r) => r.leaveId)));
    }
  };

  // Header select-all button — shown in the checkbox column header
  const SelectAllHeader = (
    <Tooltip title={allSelected ? "Deselect all" : "Select all"}>
      <Checkbox
        checked={allSelected}
        indeterminate={someSelected}
        onChange={handleSelectAll}
        icon={<CheckBoxOutlineBlankRoundedIcon sx={{ color: "#fff", fontSize: "20px" }} />}
        checkedIcon={<CheckBoxRoundedIcon sx={{ color: "#fff", fontSize: "20px" }} />}
        indeterminateIcon={<IndeterminateCheckBoxRoundedIcon sx={{ color: "#4caf50", fontSize: "20px" }} />}
        sx={{ padding: "2px" }}
      />
    </Tooltip>
  );

  // columns built inside component so header can hold the live SelectAllHeader element
  const columns = [
    { label: SelectAllHeader, field: "checkbox" },
    ...dataColumns,
  ];

  // Build display rows: prepend checkbox element to each row
  const displayRows = filteredRaw.map((row) => ({
    ...row,
    checkbox: (
      <Checkbox
        checked={selectedIds.has(row.leaveId)}
        onChange={() => handleToggleSelect(row.leaveId)}
        icon={<CheckBoxOutlineBlankRoundedIcon sx={{ color: "#D5D7DA" }} />}
        checkedIcon={<CheckBoxRoundedIcon sx={{ color: "#4caf50" }} />}
        sx={{ padding: "4px" }}
      />
    ),
    status: <StatusBadge status={row.status} />,
    actions: (
      <RowMenu
        row={row}
        onDelete={(r) => {
          setRawData((prev) => prev.filter((x) => x.leaveId !== r.leaveId));
          setToast({ open: true, severity: "success", message: "Leave record removed." });
        }}
      />
    ),
  }));

  const handleExport = () => exportToCSV(dataColumns, filteredRaw, "leave_requests.csv");

  const handleDeleteConfirm = () => {
    setRawData([]);
    setSelectedIds(new Set());
    setDeleteConfirm(false);
    setToast({ open: true, severity: "success", message: "Records cleared from view" });
  };

  const styles = {
    container: { textAlign: "left", padding: "1rem" },
    headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 },
    button: { background: "linear-gradient(90deg, #4A7BA8, #244C77)", color: "#fff", fontSize: "14px", textTransform: "none", padding: "8px 20px", borderRadius: "8px" },
    mandateButton: { backgroundColor: "#2196f3", color: "#fff", fontSize: "14px", textTransform: "none", padding: "8px 20px", borderRadius: "8px", "&:hover": { backgroundColor: "#1976d2" } },
    actionBtn: { border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px", padding: "6px 14px" },
    badge: { backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: "bold", fontSize: "12px", borderRadius: "20px", padding: "4px 12px" },
  };

  return (
    <div style={styles.container}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />

      <Dialog open={open} TransitionComponent={Transition} keepMounted sx={{ "& .MuiPaper-root": { borderRadius: "20px" } }}>
        <LeaveRequestForm handleClose={() => { setOpen(false); loadLeaves(); }} />
      </Dialog>

      <Dialog open={mandateOpen} TransitionComponent={Transition} keepMounted sx={{ "& .MuiPaper-root": { borderRadius: "20px" } }}>
        <MandateLeaveForm handleClose={() => { setMandateOpen(false); loadLeaves(); }} />
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px", mb: 1 }}>Clear Records</Typography>
          <Typography sx={{ color: "#666", fontSize: "14px", mb: 3 }}>Remove all displayed records from this view?</Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setDeleteConfirm(false)} sx={{ color: "#666", textTransform: "none", border: "1px solid #D5D7DA", borderRadius: "8px", px: 3 }}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} sx={{ backgroundColor: "#f44336", color: "#fff", textTransform: "none", borderRadius: "8px", px: 3 }}>Delete</Button>
          </Box>
        </Box>
      </Dialog>

      <p style={{ color: "rgba(0, 0, 0, 0.5)", fontWeight: "bold", fontSize: "14px", margin: 0 }}>
        Pages / Leave Management
      </p>

      <Box sx={styles.headerRow}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <h2 style={{ margin: 0 }}>Leave Management</h2>
          <Chip label={`${filteredRaw.length} Records`} sx={styles.badge} />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={() => setMandateOpen(true)} sx={styles.mandateButton}>Apply Mandate Leave</Button>
          <Button onClick={() => setOpen(true)} style={styles.button}>+ Apply Leave</Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: showFilter ? 1 : 2, flexWrap: "wrap" }}>
        <Button startIcon={<DeleteOutlineIcon />} sx={styles.actionBtn} onClick={() => setDeleteConfirm(true)}>Delete</Button>
        <Button startIcon={<FilterListIcon />} sx={{ ...styles.actionBtn, ...(showFilter ? { backgroundColor: "#f0edff", color: "#244C77", borderColor: "#244C77" } : {}) }} onClick={() => setShowFilter((v) => !v)}>Filters</Button>
        <Button startIcon={<FileDownloadOutlinedIcon />} sx={styles.actionBtn} onClick={handleExport}>Export</Button>
      </Box>

      {showFilter && (
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small" fullWidth placeholder="Search by leave ID, reason, status..."
            value={filterText} onChange={(e) => setFilterText(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#8F95B2", fontSize: "18px" }} /></InputAdornment> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: "#D5D7DA" } } }}
          />
        </Box>
      )}

      <DynamicTable columns={columns} rows={displayRows} />
    </div>
  );
};

export default LeaveRequest;
